import api from 'services/api';

class ComercialConsolidadoService {
  /**
   * Buscar dados comerciais consolidados de múltiplas empresas
   * @param {Array} companyIds - Array com nomes das empresas
   * @param {Object} filtros - Filtros de data
   * @returns {Promise<Object>} Dados consolidados
   */
  async buscarDadosConsolidados(companyIds, filtros = {}) {
    try {

      // Inicializar estrutura de dados consolidados
      const dadosConsolidados = {
        resumo: {
          total_vendas: 0,
          projecao_total_vendas: null,
          total_receita_vendas: 0,
          projecao_receita_vendas: null,
          ticket_medio: 0
        },
        evolucao_mensal: [],
        lista_inadimplencia: [],
        ranking_vendedores: [],
        clientes_os_aberta_72h: [],
        clientes_assinatura: [],
        novos_clientes: [],
        empresas: []
      };

      // Buscar dados de cada empresa
      const promises = companyIds.map(companyId => 
        this.buscarDadosEmpresa(companyId, filtros)
      );

      const resultados = await Promise.all(promises);
      
      // Processar resultados e consolidar dados
      let empresasComSucesso = 0;
      resultados.forEach((resultado, index) => {
        if (resultado.success && resultado.data) {
          empresasComSucesso++;
          const empresa = companyIds[index];
          
          // Consolidar resumo
          this.consolidarResumo(dadosConsolidados.resumo, resultado.data.resumo, empresa);
          
          // Consolidar evolução mensal
          this.consolidarEvolucaoMensal(dadosConsolidados.evolucao_mensal, resultado.data.evolucao_mensal, empresa);
          
          // Consolidar lista de inadimplência (sem agregação entre empresas)
          this.consolidarListaInadimplencia(dadosConsolidados.lista_inadimplencia, resultado.data.lista_inadimplencia, empresa);

          // Consolidar clientes com OS aberta >72h
          const clientesOsAberta72h = resultado.data.clientes_os_aberta_72h || resultado.data.novos_clientes;
          this.consolidarClientesOsAberta72h(dadosConsolidados.clientes_os_aberta_72h, clientesOsAberta72h, empresa);
          const clientesAssinatura = resultado.data.clientes_assinatura || [];
          this.consolidarClientesOsAberta72h(dadosConsolidados.clientes_assinatura, clientesAssinatura, empresa);
          
          // Adicionar dados da empresa
          dadosConsolidados.empresas.push({
            nome: empresa,
            cor: this.getCompanyColor(empresa),
            resumo: resultado.data.resumo,
            evolucao_mensal: resultado.data.evolucao_mensal,
            lista_inadimplencia: resultado.data.lista_inadimplencia || [],
            ranking_vendedores: resultado.data.ranking_vendedores || [],
            clientes_os_aberta_72h: clientesOsAberta72h,
            clientes_assinatura: clientesAssinatura,
            novos_clientes: clientesOsAberta72h
          });
        } else {
          console.warn(`⚠️ Erro ao processar dados da empresa ${companyIds[index]}:`, resultado.error);
        }
      });


      // Calcular ticket médio consolidado
      if (dadosConsolidados.resumo.total_vendas > 0) {
        dadosConsolidados.resumo.ticket_medio = dadosConsolidados.resumo.total_receita_vendas / dadosConsolidados.resumo.total_vendas;
      }

      // Ordenar lista de inadimplência por maior desconto
      dadosConsolidados.lista_inadimplencia.sort((a, b) => Number(b.value || 0) - Number(a.value || 0));

      // Ordenar clientes com OS aberta >72h por data
      dadosConsolidados.clientes_os_aberta_72h.sort((a, b) => new Date(b.data) - new Date(a.data));
      dadosConsolidados.clientes_assinatura.sort((a, b) => new Date(b.data) - new Date(a.data));
      // Alias legado de compatibilidade
      dadosConsolidados.novos_clientes = dadosConsolidados.clientes_os_aberta_72h;

      
      return {
        success: true,
        data: dadosConsolidados,
        empresas_processadas: empresasComSucesso,
        total_empresas: companyIds.length
      };

    } catch (error) {
      console.error('❌ Erro ao consolidar dados comerciais:', error);
      throw error;
    }
  }

  /**
   * Consolidar lista de inadimplência sem agregar vendedores entre empresas
   */
  consolidarListaInadimplencia(consolidado, inadimplentes, empresa) {
    if (!inadimplentes || !Array.isArray(inadimplentes)) return;

    inadimplentes.forEach((item) => {
      consolidado.push({
        ...item,
        empresa,
      });
    });
  }

  /**
   * Buscar dados de uma empresa específica
   */
  async buscarDadosEmpresa(companyId, filtros) {
    try {
      const params = {
        empresa: companyId,
        data_inicio: filtros.data_inicio || this.getDataInicioMes(),
        data_fim: filtros.data_fim || this.getDataFimMes()
      };

      const response = await api.get('/n8n/comercial/dashboard-completo-cache', { params });
      
      
      if (response.data && response.data.success && response.data.data) {
        return {
          success: true,
          companyName: companyId,
          data: response.data.data
        };
      } else {
        console.error(`❌ Resposta inválida para ${companyId}:`, response.data);
        throw new Error(`Erro na resposta da API para ${companyId}: ${response.data?.message || 'Resposta inválida'}`);
      }
    } catch (error) {
      console.error(`❌ Erro ao buscar dados da empresa ${companyId}:`, error);
      return {
        success: false,
        companyName: companyId,
        data: null,
        error: error.message
      };
    }
  }

  /**
   * Consolidar resumo comercial
   */
  consolidarResumo(consolidado, resumo, _empresa) {
    if (!resumo) return;

    consolidado.total_vendas += resumo.total_vendas || 0;
    consolidado.total_receita_vendas += resumo.total_receita_vendas || 0;

    // As projeções somam PRESERVANDO `null`. O backend devolve `null` quando o período está fechado
    // ou é futuro — "não se projeta" —, e somar com `|| 0` transformaria isso em zero, fazendo a
    // tela exibir um card de projeção zerada em vez de ocultá-lo.
    //
    // Só vira número se ao menos uma empresa tiver projeção; as que não têm entram como zero na
    // soma, e não anulam as demais.
    consolidado.projecao_total_vendas = this.somarProjecao(
      consolidado.projecao_total_vendas,
      resumo.projecao_total_vendas,
    );
    consolidado.projecao_receita_vendas = this.somarProjecao(
      consolidado.projecao_receita_vendas,
      resumo.projecao_receita_vendas,
    );
  }

  /**
   * `null + null = null`; qualquer número presente faz a soma virar número.
   */
  somarProjecao(acumulado, valor) {
    if (valor === null || valor === undefined) return acumulado;
    return (acumulado ?? 0) + (Number(valor) || 0);
  }

  /**
   * Consolidar evolução mensal
   */
  consolidarEvolucaoMensal(consolidado, evolucao, _empresa) {
    if (!evolucao || !Array.isArray(evolucao)) return;


    evolucao.forEach(item => {
      const mesExistente = consolidado.find(m => m.mes === item.mes);
      
      if (mesExistente) {
        // Inicializar receita_instalacao se não existir (para compatibilidade com dados antigos)
        if (mesExistente.receita_instalacao === undefined) {
          mesExistente.receita_instalacao = 0;
        }
        
        // Somar aos dados existentes
        mesExistente.receita_total += item.receita_total || 0;
        mesExistente.receita_instalacao += item.receita_instalacao || 0;
        mesExistente.total_contratos += item.total_contratos || 0;
        mesExistente.ticket_medio = mesExistente.total_contratos > 0 
          ? mesExistente.receita_total / mesExistente.total_contratos 
          : 0;
        
        // Adicionar dados da empresa específica
        if (!mesExistente.empresas) {
          mesExistente.empresas = {};
        }
        mesExistente.empresas[_empresa] = {
          receita_total: item.receita_total || 0,
          receita_instalacao: item.receita_instalacao || 0,
          total_contratos: item.total_contratos || 0,
          ticket_medio: item.ticket_medio || 0
        };
        
      } else {
        // Adicionar novo mês
        consolidado.push({
          mes: item.mes,
          receita_total: item.receita_total || 0,
          receita_instalacao: item.receita_instalacao || 0,
          total_contratos: item.total_contratos || 0,
          ticket_medio: item.ticket_medio || 0,
          empresas: {
              [_empresa]: {
              receita_total: item.receita_total || 0,
              receita_instalacao: item.receita_instalacao || 0,
              total_contratos: item.total_contratos || 0,
              ticket_medio: item.ticket_medio || 0
            }
          }
        });
      }
    });

    // Ordenar por mês
    consolidado.sort((a, b) => {
      const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      return meses.indexOf(a.mes) - meses.indexOf(b.mes);
    });
  }

  /**
   * Consolidar ranking de vendedores
   */
  consolidarRankingVendedores(consolidado, vendedores, _empresa) {
    if (!vendedores || !Array.isArray(vendedores)) return;


    vendedores.forEach(vendedor => {
      const vendedorExistente = consolidado.find(v => v.nome === vendedor.nome);
      
      if (vendedorExistente) {
        // Somar aos dados existentes
        vendedorExistente.contratos += vendedor.contratos || 0;
        vendedorExistente.receita += vendedor.receita || 0;
        vendedorExistente.ticket_medio = vendedorExistente.contratos > 0 
          ? vendedorExistente.receita / vendedorExistente.contratos 
          : 0;
        
        // Adicionar empresa se não existir na lista
        if (!vendedorExistente.empresas) {
          vendedorExistente.empresas = [];
        }
        if (!vendedorExistente.empresas.includes(_empresa)) {
          vendedorExistente.empresas.push(_empresa);
        }
      } else {
        // Adicionar novo vendedor
        consolidado.push({
          nome: vendedor.nome,
          contratos: vendedor.contratos || 0,
          receita: vendedor.receita || 0,
          ticket_medio: vendedor.ticket_medio || 0,
          empresas: [_empresa]
        });
      }
    });
  }

  /**
   * Consolidar clientes com OS aberta >72h
   */
  consolidarClientesOsAberta72h(consolidado, clientes, _empresa) {
    if (!clientes || !Array.isArray(clientes)) return;


    clientes.forEach(cliente => {
      consolidado.push({
        ...cliente,
        empresa: _empresa // Adicionar identificação da empresa
      });
    });
  }

  consolidarNovosClientes(consolidado, clientes, empresa) {
    // Alias legado para compatibilidade temporária.
    this.consolidarClientesOsAberta72h(consolidado, clientes, empresa);
  }

  /**
   * Métodos auxiliares para datas
   */
  getDataInicioMes() {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
  }

  getDataFimMes() {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];
  }

  /**
   * Obter cor da empresa
   */
  getCompanyColor(companyName) {
    const cores = {
      'MOV Fibra': '#8B5CF6',
      'XT Fibra': '#DC2626',
      'Horizon Group': '#3B82F6'
    };
    return cores[companyName] || '#6B7280';
  }
}

const comercialConsolidadoService = new ComercialConsolidadoService();
export default comercialConsolidadoService;
